import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Target, Clock, Award, Calendar } from 'lucide-react';
import { studentAPI } from '../../services/api';

const PerformanceAnalytics = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState('week'); // week, month, year

    useEffect(() => {
        fetchPerformanceData();
    }, [selectedPeriod]);

    const fetchPerformanceData = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await studentAPI.getPerformance({ period: selectedPeriod });
            if (response.data && Object.keys(response.data).length > 0) {
                setAnalytics(response.data);
            } else {
                // Use fallback only if API returns empty
                setAnalytics(getEmptyState());
            }
        } catch (err) {
            setError('Failed to load analytics. Using sample data.');
            console.error('Error fetching performance:', err);
            setAnalytics(getEmptyState());
        } finally {
            setLoading(false);
        }
    };

    const getEmptyState = () => ({
        overall_score: 0,
        confidence_index: 0,
        total_sessions: 0,
        total_practice_time: 0,
        improvement_rate: 0,
        skills: {
            fluency: { score: 0, change: 0, lessons_completed: 0 },
            grammar: { score: 0, change: 0, lessons_completed: 0 },
            pronunciation: { score: 0, change: 0, lessons_completed: 0 },
            vocabulary: { score: 0, change: 0, lessons_completed: 0 }
        },
        weekly_activity: [],
        achievements_unlocked: 0,
        streak_days: 0
    });



    const getScoreColor = (score) => {
        if (score >= 80) return '#27B05B';
        if (score >= 60) return '#FF9800';
        return '#E74C3C';
    };

    const renderSkillCard = (skillName, skillData) => {
        const skillIcons = {
            fluency: '🗣️',
            grammar: '📝',
            pronunciation: '🔊',
            vocabulary: '📚'
        };

        return (
            <div key={skillName} style={styles.skillCard}>
                <div style={styles.skillHeader}>
                    <span style={styles.skillIcon}>{skillIcons[skillName]}</span>
                    <div style={styles.skillInfo}>
                        <h3 style={styles.skillName}>{skillName.charAt(0).toUpperCase() + skillName.slice(1)}</h3>
                        <p style={styles.skillLessons}>{skillData.lessons_completed} lessons completed</p>
                    </div>
                </div>

                <div style={styles.skillScore}>
                    <div style={styles.scoreCircle}>
                        <svg width="120" height="120">
                            <circle cx="60" cy="60" r="50" fill="none" stroke="#E0E0E0" strokeWidth="10" />
                            <circle 
                                cx="60" 
                                cy="60" 
                                r="50" 
                                fill="none" 
                                stroke={getScoreColor(skillData.score)}
                                strokeWidth="10"
                                strokeDasharray={`${(skillData.score / 100) * 314.159} 314.159`}
                                transform="rotate(-90 60 60)"
                            />
                        </svg>
                        <div style={styles.scoreValue}>{skillData.score}%</div>
                    </div>

                    <div style={styles.changeIndicator}>
                        <TrendingUp size={16} color={skillData.change > 0 ? '#27B05B' : '#E74C3C'} />
                        <span style={{color: skillData.change > 0 ? '#27B05B' : '#E74C3C'}}>
                            {skillData.change > 0 ? '+' : ''}{skillData.change}% this {selectedPeriod}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderWeeklyActivity = () => {
        const maxSessions = Math.max(...analytics.weekly_activity.map(d => d.sessions));
        
        return (
            <div style={styles.activitySection}>
                <h3 style={styles.sectionTitle}>Weekly Activity</h3>
                <div style={styles.activityChart}>
                    {analytics.weekly_activity.map(day => (
                        <div key={day.day} style={styles.activityBar}>
                            <div 
                                style={{
                                    ...styles.bar,
                                    height: `${(day.sessions / maxSessions) * 100}%`,
                                    backgroundColor: day.sessions > 2 ? '#27B05B' : '#95A5A6'
                                }}
                            >
                                <span style={styles.barValue}>{day.sessions}</span>
                            </div>
                            <span style={styles.barLabel}>{day.day}</span>
                            <span style={styles.barTime}>{day.minutes}min</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Loading your analytics...</p>
                </div>
            </div>
        );
    }

    if (error && !analytics) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>
                    <p>{error}</p>
                    <button onClick={fetchPerformanceData} style={styles.retryButton}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Performance Analytics</h1>
                    <p style={styles.subtitle}>Track your learning progress and improvements</p>
                </div>

                <div style={styles.periodSelector}>
                    <button 
                        onClick={() => setSelectedPeriod('week')}
                        style={{...styles.periodButton, ...(selectedPeriod === 'week' ? styles.periodButtonActive : {})}}
                    >
                        Week
                    </button>
                    <button 
                        onClick={() => setSelectedPeriod('month')}
                        style={{...styles.periodButton, ...(selectedPeriod === 'month' ? styles.periodButtonActive : {})}}
                    >
                        Month
                    </button>
                    <button 
                        onClick={() => setSelectedPeriod('year')}
                        style={{...styles.periodButton, ...(selectedPeriod === 'year' ? styles.periodButtonActive : {})}}
                    >
                        Year
                    </button>
                </div>
            </div>

            <div style={styles.statsOverview}>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#E8F5E9'}}>
                        <Target size={28} color="#27B05B" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{analytics.overall_score}%</div>
                        <div style={styles.statLabel}>Overall Score</div>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#E3F2FD'}}>
                        <BarChart3 size={28} color="#3498DB" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{analytics.confidence_index}%</div>
                        <div style={styles.statLabel}>Confidence Index</div>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#FFF3E0'}}>
                        <Clock size={28} color="#FF9800" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{Math.round(analytics.total_practice_time / 60)}h</div>
                        <div style={styles.statLabel}>Practice Time</div>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#F3E5F5'}}>
                        <TrendingUp size={28} color="#9C27B0" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>+{analytics.improvement_rate}%</div>
                        <div style={styles.statLabel}>Improvement Rate</div>
                    </div>
                </div>
            </div>

            <div style={styles.skillsSection}>
                <h2 style={styles.sectionHeading}>Skills Breakdown</h2>
                <div style={styles.skillsGrid}>
                    {Object.entries(analytics.skills).map(([skillName, skillData]) => 
                        renderSkillCard(skillName, skillData)
                    )}
                </div>
            </div>

            {renderWeeklyActivity()}

            <div style={styles.achievementsSection}>
                <div style={styles.achievementCard}>
                    <Award size={32} color="#FFD700" />
                    <div>
                        <div style={styles.achievementValue}>{analytics.achievements_unlocked}</div>
                        <div style={styles.achievementLabel}>Achievements Unlocked</div>
                    </div>
                </div>

                <div style={styles.achievementCard}>
                    <Calendar size={32} color="#27B05B" />
                    <div>
                        <div style={styles.achievementValue}>{analytics.streak_days} Days</div>
                        <div style={styles.achievementLabel}>Current Streak 🔥</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '20px'
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    subtitle: {
        fontSize: '16px',
        color: '#666',
        marginTop: '8px'
    },
    periodSelector: {
        display: 'flex',
        backgroundColor: 'white',
        padding: '5px',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    periodButton: {
        padding: '10px 25px',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#666',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    periodButtonActive: {
        backgroundColor: '#27B05B',
        color: 'white'
    },
    statsOverview: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '40px'
    },
    statCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        padding: '25px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    statIcon: {
        width: '60px',
        height: '60px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    statContent: {},
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    statLabel: {
        fontSize: '14px',
        color: '#666',
        marginTop: '5px'
    },
    skillsSection: {
        marginBottom: '40px'
    },
    sectionHeading: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '20px'
    },
    skillsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px'
    },
    skillCard: {
        backgroundColor: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    skillHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px'
    },
    skillIcon: {
        fontSize: '32px'
    },
    skillInfo: {},
    skillName: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#2C3E50',
        marginBottom: '4px'
    },
    skillLessons: {
        fontSize: '13px',
        color: '#666'
    },
    skillScore: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '15px'
    },
    scoreCircle: {
        position: 'relative',
        width: '120px',
        height: '120px'
    },
    scoreValue: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '24px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    changeIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: '600'
    },
    activitySection: {
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '40px'
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '25px'
    },
    activityChart: {
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'flex-end',
        height: '250px',
        gap: '10px'
    },
    activityBar: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1'
    },
    bar: {
        width: '100%',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '10px',
        minHeight: '40px',
        transition: 'all 0.3s ease'
    },
    barValue: {
        color: 'white',
        fontWeight: '700',
        fontSize: '14px'
    },
    barLabel: {
        marginTop: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#666'
    },
    barTime: {
        fontSize: '12px',
        color: '#999',
        marginTop: '4px'
    },
    achievementsSection: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px'
    },
    achievementCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        padding: '30px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    achievementValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    achievementLabel: {
        fontSize: '15px',
        color: '#666',
        marginTop: '5px'
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    error: {
        textAlign: 'center',
        padding: '50px',
        color: '#E74C3C'
    },
    retryButton: {
        marginTop: '20px',
        padding: '12px 30px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer'
    }
};

export default PerformanceAnalytics;
